﻿import { useState, useEffect } from 'react';
import { useAuthStore } from '../hooks/useAuthStore';

// Inject Google Fonts for Manrope + Inter + Material Symbols
const fontLink = document.createElement('link');
fontLink.rel = 'stylesheet';
fontLink.href = 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Inter:wght@300;400;500;600&display=swap';
if (!document.head.querySelector('[href*="Manrope"]')) document.head.appendChild(fontLink);

const msLink = document.createElement('link');
msLink.rel = 'stylesheet';
msLink.href = 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200';
if (!document.head.querySelector('[href*="Material+Symbols"]')) document.head.appendChild(msLink);

const ADMIN_STYLES = `
  .admin-wrap { font-family: 'Inter', sans-serif; background: #f8f9fd; min-height: 100vh; display: flex; }
  .admin-sidebar {
    width: 256px; min-height: 100vh; background: #22005d;
    position: fixed; left: 0; top: 0; bottom: 0; overflow-y: auto;
    display: flex; flex-direction: column; padding: 24px 0; z-index: 50;
    box-shadow: 4px 0 24px rgba(34,0,93,0.18);
  }
  .admin-sidebar::-webkit-scrollbar { width: 4px; }
  .admin-sidebar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 4px; }
  .sidebar-brand { padding: 0 24px 32px; }
  .sidebar-brand-inner { display: flex; align-items: center; gap: 12px; }
  .sidebar-logo { width: 40px; height: 40px; border-radius: 12px; background: #6200ee; display: flex; align-items: center; justify-content: center; }
  .sidebar-logo .material-symbols-outlined { color: white; font-size: 22px; }
  .sidebar-brand-text h1 { font-family: 'Manrope', sans-serif; font-size: 16px; font-weight: 800; color: white; line-height: 1.1; }
  .sidebar-brand-text p { font-size: 9px; color: #cfbdff; text-transform: uppercase; letter-spacing: 3px; font-weight: 700; margin-top: 2px; }
  .sidebar-nav { flex: 1; padding: 0 12px; }
  .sidebar-section-label { font-size: 9px; color: rgba(255,255,255,0.35); text-transform: uppercase; letter-spacing: 2.5px; font-weight: 700; padding: 16px 16px 8px; }
  .sidebar-item {
    display: flex; align-items: center; gap: 12px; padding: 10px 16px;
    color: rgba(255,255,255,0.6); border-radius: 8px; cursor: pointer;
    font-size: 13.5px; font-weight: 500; transition: all 0.18s;
    border: none; background: none; width: 100%; text-align: left; font-family: 'Manrope', sans-serif;
    text-decoration: none;
  }
  .sidebar-item:hover { color: white; background: rgba(255,255,255,0.07); }
  .sidebar-item.active { color: white; background: rgba(98,0,238,0.18); border-left: 3px solid #6200ee; margin-left: -3px; }
  .sidebar-item .material-symbols-outlined { font-size: 20px; }
  .sidebar-divider { border-top: 1px solid rgba(255,255,255,0.1); margin: 16px 16px 0; }
  .sidebar-footer-item { font-size: 12px !important; padding: 8px 16px !important; }

  .admin-main { margin-left: 256px; flex: 1; display: flex; flex-direction: column; min-height: 100vh; }
  .admin-topbar {
    position: sticky; top: 0; right: 0; z-index: 40;
    background: rgba(255,255,255,0.85); backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(203,195,217,0.25);
    height: 64px; display: flex; align-items: center; justify-content: space-between; padding: 0 32px;
  }
  .topbar-search { position: relative; max-width: 320px; flex: 1; }
  .topbar-search .material-symbols-outlined { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #7a7488; font-size: 18px; }
  .topbar-search input {
    width: 100%; background: #e7e8ec; border: none; border-radius: 24px;
    padding: 8px 16px 8px 40px; font-size: 13px; outline: none; font-family: 'Inter', sans-serif;
    transition: all 0.2s;
  }
  .topbar-search input:focus { background: white; box-shadow: 0 0 0 2px rgba(98,0,238,0.15); }
  .topbar-right { display: flex; align-items: center; gap: 20px; }
  .topbar-user { display: flex; align-items: center; gap: 12px; border-left: 1px solid rgba(203,195,217,0.4); padding-left: 20px; }
  .topbar-user-name { font-size: 13px; font-weight: 700; color: #191c1f; }
  .topbar-user-role { font-size: 9px; color: #7a7488; text-transform: uppercase; letter-spacing: 1px; font-weight: 600; }
  .topbar-avatar { width: 36px; height: 36px; border-radius: 50%; background: linear-gradient(135deg, #6200ee, #22005d); display: flex; align-items: center; justify-content: center; color: white; font-size: 13px; font-weight: 700; }

  .admin-content { padding: 28px 32px 48px; }
  .page-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; }
  .page-greeting { font-size: 11px; font-weight: 700; color: #7a7488; text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 4px; }
  .page-title { font-family: 'Manrope', sans-serif; font-size: 22px; font-weight: 800; color: #191c1f; }
  .page-actions { display: flex; gap: 10px; }

  .hero-banner {
    background: linear-gradient(135deg, #4800b2 0%, #6200ee 100%);
    border-radius: 20px; padding: 28px 32px; color: white; margin-bottom: 28px;
    position: relative; overflow: hidden;
    box-shadow: 0 8px 32px rgba(98,0,238,0.25);
  }
  .hero-badge { display: inline-flex; align-items: center; gap: 8px; background: rgba(255,255,255,0.12); backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.2); border-radius: 20px; padding: 5px 12px; margin-bottom: 12px; }
  .hero-pulse { width: 8px; height: 8px; background: #4ade80; border-radius: 50%; animation: pulse 1.5s infinite; }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
  .hero-badge span { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; }
  .hero-banner h2 { font-family: 'Manrope', sans-serif; font-size: 22px; font-weight: 800; margin-bottom: 6px; }
  .hero-banner p { font-size: 13px; opacity: 0.8; max-width: 500px; }
  .hero-deco { position: absolute; right: -40px; top: -60px; width: 220px; height: 220px; background: rgba(255,255,255,0.04); border-radius: 50%; }
  .hero-deco2 { position: absolute; right: 80px; bottom: -80px; width: 160px; height: 160px; background: rgba(255,255,255,0.07); border-radius: 50%; }

  .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 28px; }
  .stat-card { background: white; border-radius: 20px; padding: 22px; border: 1px solid #e7e8ec; box-shadow: 0 2px 8px rgba(0,0,0,0.04); transition: all 0.2s; }
  .stat-card:hover { box-shadow: 0 6px 20px rgba(0,0,0,0.08); transform: translateY(-1px); }
  .stat-card-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 14px; }
  .stat-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
  .stat-icon .material-symbols-outlined { font-size: 20px; font-variation-settings: 'FILL' 0; }
  .stat-trend { font-size: 11px; font-weight: 700; display: flex; align-items: center; gap: 2px; }
  .trend-up { color: #22c55e; }
  .trend-down { color: #ef4444; }
  .stat-value { font-size: 26px; font-weight: 800; font-family: 'Manrope', sans-serif; color: #191c1f; margin-bottom: 4px; line-height: 1; }
  .stat-label { font-size: 11px; font-weight: 600; color: #7a7488; }
  .stat-note { font-size: 10px; color: #7a7488; opacity: 0.6; margin-top: 6px; }

  .content-grid { display: grid; grid-template-columns: 1fr 320px; gap: 24px; margin-bottom: 28px; }
  .s-card { background: white; border-radius: 20px; border: 1px solid #e7e8ec; box-shadow: 0 2px 8px rgba(0,0,0,0.04); overflow: hidden; }
  .s-card-header { padding: 18px 22px; border-bottom: 1px solid #edeef2; display: flex; align-items: center; justify-content: space-between; }
  .s-card-title { font-family: 'Manrope', sans-serif; font-size: 15px; font-weight: 800; color: #191c1f; }
  .s-card-body { padding: 20px 22px; }

  .admin-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .admin-table th { padding: 11px 16px; background: #f2f3f7; color: #7a7488; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; text-align: left; }
  .admin-table td { padding: 13px 16px; color: #494456; border-bottom: 1px solid #f2f3f7; }
  .admin-table tr:last-child td { border-bottom: none; }
  .admin-table tbody tr:hover { background: #f8f9fd; }
  .user-cell { display: flex; align-items: center; gap: 10px; }
  .user-avatar { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; color: white; flex-shrink: 0; }
  .user-name { font-weight: 600; color: #191c1f; font-size: 13px; }
  .user-email { font-size: 11px; color: #7a7488; }

  .pill { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; }
  .pill-purple { background: #e8ddff; color: #4800b2; }
  .pill-green  { background: #dcfce7; color: #15803d; }
  .pill-teal   { background: #ccfbf1; color: #0f766e; }
  .pill-orange { background: #ffedd5; color: #c2410c; }
  .pill-red    { background: #fee2e2; color: #b91c1c; }
  .pill-gray   { background: #f3f4f6; color: #4b5563; }
  .pill-blue   { background: #dbeafe; color: #1d4ed8; }

  .btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 18px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; border: none; transition: all 0.18s; font-family: 'Manrope', sans-serif; }
  .btn-sm { padding: 5px 12px; font-size: 11px; border-radius: 6px; }
  .btn-primary { background: #4800b2; color: white; }
  .btn-primary:hover { background: #3800a0; filter: brightness(1.05); }
  .btn-outline { background: transparent; border: 1.5px solid #4800b2; color: #4800b2; }
  .btn-outline:hover { background: #f0e6ff; }
  .btn-danger { background: #fee2e2; color: #b91c1c; }
  .btn-danger:hover { background: #fecaca; }
  .btn-success { background: #dcfce7; color: #15803d; }
  .btn-success:hover { background: #bbf7d0; }
  .btn-neutral { background: #edeef2; color: #494456; }
  .btn-neutral:hover { background: #e1e2e6; }
  .btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .search-row { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
  .admin-input { background: #edeef2; border: 1.5px solid transparent; border-radius: 10px; padding: 9px 14px; font-size: 13px; color: #191c1f; outline: none; font-family: 'Inter', sans-serif; transition: all 0.2s; }
  .admin-input:focus { border-color: #6200ee; background: white; box-shadow: 0 0 0 3px rgba(98,0,238,0.08); }
  .admin-select { background: #edeef2; border: 1.5px solid transparent; border-radius: 10px; padding: 9px 14px; font-size: 13px; color: #191c1f; outline: none; font-family: 'Inter', sans-serif; cursor: pointer; transition: all 0.2s; }
  .admin-select:focus { border-color: #6200ee; background: white; }

  .alert { padding: 12px 16px; border-radius: 12px; margin-bottom: 16px; display: flex; align-items: center; gap: 10px; font-size: 13px; font-weight: 500; }
  .alert-success { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }
  .alert-error   { background: #fee2e2; color: #b91c1c; border: 1px solid #fecaca; }

  .modal-bg { position: fixed; inset: 0; background: rgba(34,0,93,0.45); z-index: 200; display: flex; align-items: center; justify-content: center; padding: 20px; backdrop-filter: blur(4px); }
  .modal { background: white; border-radius: 20px; width: 100%; max-width: 560px; max-height: 88vh; overflow-y: auto; box-shadow: 0 24px 64px rgba(34,0,93,0.2); }
  .modal-lg { max-width: 700px; }
  .modal-header { padding: 20px 24px; border-bottom: 1px solid #edeef2; display: flex; align-items: center; justify-content: space-between; }
  .modal-title { font-family: 'Manrope', sans-serif; font-size: 16px; font-weight: 800; color: #191c1f; }
  .modal-body { padding: 24px; }
  .modal-close { background: #edeef2; border: none; border-radius: 8px; width: 32px; height: 32px; cursor: pointer; display: flex; align-items: center; justify-content: center; color: #7a7488; font-size: 18px; }
  .modal-close:hover { background: #e1e2e6; }

  .form-label { display: block; font-size: 12px; font-weight: 600; color: #494456; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px; }
  .form-input { width: 100%; background: #f2f3f7; border: 1.5px solid transparent; border-radius: 10px; padding: 10px 14px; font-size: 13px; color: #191c1f; outline: none; font-family: 'Inter', sans-serif; transition: all 0.2s; }
  .form-input:focus { border-color: #6200ee; background: white; box-shadow: 0 0 0 3px rgba(98,0,238,0.08); }
  .form-group { margin-bottom: 16px; }

  .toggle { position: relative; display: inline-block; width: 44px; height: 24px; }
  .toggle input { display: none; }
  .toggle-track { position: absolute; inset: 0; background: #d1d5db; border-radius: 24px; cursor: pointer; transition: 0.3s; }
  .toggle-track::before { content: ''; position: absolute; width: 18px; height: 18px; left: 3px; top: 3px; background: white; border-radius: 50%; transition: 0.3s; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
  .toggle input:checked + .toggle-track { background: #6200ee; }
  .toggle input:checked + .toggle-track::before { transform: translateX(20px); }

  .app-item { display: flex; align-items: center; justify-content: space-between; padding: 14px; background: #f8f9fd; border-radius: 12px; margin-bottom: 10px; border: 1px solid #edeef2; transition: border-color 0.2s; }
  .app-item:hover { border-color: #cfbdff; }

  .pagination { display: flex; gap: 6px; align-items: center; }
  .page-btn { padding: 6px 14px; border: 1.5px solid #e1e2e6; border-radius: 8px; background: white; font-size: 12px; font-weight: 600; cursor: pointer; color: #494456; transition: all 0.15s; }
  .page-btn:hover:not(:disabled) { border-color: #6200ee; color: #6200ee; }
  .page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  .activity-item { display: flex; gap: 14px; padding: 14px 0; border-bottom: 1px solid #f2f3f7; }
  .activity-item:last-child { border-bottom: none; }
  .activity-icon { width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .activity-icon .material-symbols-outlined { font-size: 18px; }

  .platform-item { display: flex; align-items: center; justify-content: space-between; padding: 10px 12px; background: white; border-radius: 12px; border: 1px solid #edeef2; margin-bottom: 8px; }

  .tools-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; }
  .tool-card { background: white; border: 1px solid #e7e8ec; border-radius: 16px; padding: 18px 12px; text-align: center; cursor: pointer; transition: all 0.2s; }
  .tool-card:hover { border-color: rgba(98,0,238,0.3); box-shadow: 0 4px 12px rgba(98,0,238,0.08); }
  .tool-icon { width: 48px; height: 48px; border-radius: 14px; display: flex; align-items: center; justify-content: center; margin: 0 auto 10px; }
  .tool-icon .material-symbols-outlined { font-size: 22px; font-variation-settings: 'FILL' 0; }
  .tool-name { font-size: 11px; font-weight: 700; color: #191c1f; }
  .tool-sub { font-size: 9px; color: #7a7488; text-transform: uppercase; margin-top: 2px; }

  @media (max-width: 1024px) { .stats-grid { grid-template-columns: repeat(2, 1fr); } .content-grid { grid-template-columns: 1fr; } .tools-grid { grid-template-columns: repeat(3, 1fr); } }
  @media (max-width: 768px) { .admin-sidebar { transform: translateX(-100%); } .admin-main { margin-left: 0; } .admin-content { padding: 20px 16px; } }
`;

const AVATAR_COLORS = ['#6200ee','#0d9488','#f97316','#ec4899','#3b82f6','#8b5cf6','#10b981','#f59e0b'];
const getAvatarColor = (s = '') => AVATAR_COLORS[(s.charCodeAt(0) || 0) % AVATAR_COLORS.length];
const getInitials = (s = '') => (s || '?').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();

const AI_TOOLS = [
  { name: 'ID Disease AI', sub: 'Infectious Disease', icon: 'coronavirus', bg: '#fef2f2', color: '#ef4444' },
  { name: 'General Medicine', sub: 'GPT-4 Powered', icon: 'medical_information', bg: '#f0fdf4', color: '#22c55e' },
  { name: 'Voice Agent', sub: 'Voice Consult', icon: 'mic', bg: '#faf5ff', color: '#a855f7' },
  { name: 'Ask Dr Sam', sub: 'AI Doctor Chat', icon: 'psychiatry', bg: '#eff6ff', color: '#3b82f6' },
  { name: 'Medical Letters', sub: 'AI Correspondence', icon: 'description', bg: '#fff7ed', color: '#f97316' },
  { name: 'Video Consult', sub: 'Real-time Video', icon: 'video_call', bg: '#eef2ff', color: '#6366f1' },
  { name: 'MCQ Test', sub: 'Knowledge Assessment', icon: 'quiz', bg: '#f0fdfa', color: '#14b8a6' },
  { name: 'Book Appt', sub: 'Schedule Patients', icon: 'calendar_month', bg: '#fdf4ff', color: '#d946ef' },
];

const PLATFORMS = [
  { name: 'Infectious Disease AI', tag: 'Custom AI', tagClass: 'pill-red' },
  { name: 'General Medicine AI', tag: 'OpenAI GPT-4', tagClass: 'pill-green' },
  { name: 'Voice Agent', tag: 'OpenAI Realtime', tagClass: 'pill-purple' },
  { name: 'Video Consultation', tag: '100ms', tagClass: 'pill-blue' },
];

export default function Admin() {
  const { token } = useAuthStore();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ firstName: '', lastName: '', email: '', password: '', role: 'patient', specialisation: '', phone: '', selectedApps: [] });
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [availableApps, setAvailableApps] = useState([]);
  const [selectedUserApps, setSelectedUserApps] = useState(null);
  const [showAppAccessModal, setShowAppAccessModal] = useState(false);
  const [emailTest, setEmailTest] = useState({ to: '', toName: '', template: 'custom', subject: 'Test Email from InstantDrConsult Admin', body: 'Hello,\n\nThis is a test email sent from the InstantDrConsult admin dashboard.\n\nRegards,\nInstantDrConsult Team' });
  const [emailTestLoading, setEmailTestLoading] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);

  const fetchAvailableApps = async () => {
    try {
      const res = await fetch('/api/admin/apps', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setAvailableApps(d.apps || []); }
    } catch {}
  };

  const fetchStats = async () => {
    try {
      const res = await fetch('/api/admin/stats', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setStats(d); }
    } catch {}
  };

  const fetchUsers = async (page = 1, search = '', role = '') => {
    setIsLoading(true);
    try {
      const p = new URLSearchParams({ page: page.toString(), limit: '10', search, role });
      const res = await fetch(`/api/admin/users?${p}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setUsers(d.users); setPagination(d.pagination); }
      else setError('Failed to fetch users');
    } catch { setError('Error fetching users'); }
    finally { setIsLoading(false); }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault(); setIsLoading(true); setError(''); setSuccess('');
    try {
      const url = editingUser ? `/api/admin/users/${editingUser.id}` : '/api/admin/users';
      const res = await fetch(url, { method: editingUser ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(userForm) });
      const d = await res.json();
      if (res.ok) {
        const userId = editingUser ? editingUser.id : d.userId;
        if (userForm.selectedApps.length > 0) {
          await fetch(`/api/admin/users/${userId}/app-access`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ appAccess: userForm.selectedApps.map(k => ({ appKey: k, isEnabled: true })) }) });
        }
        setSuccess(editingUser ? 'User updated successfully' : 'User created successfully');
        setShowUserForm(false); setEditingUser(null);
        setUserForm({ firstName: '', lastName: '', email: '', password: '', role: 'patient', specialisation: '', phone: '', selectedApps: [] });
        fetchUsers(pagination.page, searchTerm, roleFilter);
      } else setError(d.error || 'Failed to save user');
    } catch { setError('Error saving user'); }
    finally { setIsLoading(false); }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Delete this user?')) return;
    setIsLoading(true);
    try {
      const res = await fetch(`/api/admin/users/${userId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { setSuccess('User deleted'); fetchUsers(pagination.page, searchTerm, roleFilter); }
      else { const d = await res.json(); setError(d.error || 'Failed to delete'); }
    } catch { setError('Error deleting user'); }
    finally { setIsLoading(false); }
  };

  const handleEditUser = async (user) => {
    setEditingUser(user);
    const validRoles = ['patient', 'ward_clerk', 'doctor', 'hospital_admin', 'admin', 'user'];
    const userRole = validRoles.includes(user.role) ? user.role : 'patient';
    try {
      const res = await fetch(`/api/admin/users/${user.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        const enabledKeys = d.appAccess.filter(a => a.is_enabled).map(a => a.app_key);
        setUserForm({ firstName: user.first_name, lastName: user.last_name, email: user.email, password: '', role: userRole, specialisation: user.specialisation || '', phone: user.phone || '', selectedApps: enabledKeys });
      }
    } catch {
      setUserForm({ firstName: user.first_name, lastName: user.last_name, email: user.email, password: '', role: userRole, specialisation: user.specialisation || '', phone: user.phone || '', selectedApps: [] });
    }
    setShowUserForm(true);
  };

  const fetchUserAppAccess = async (userId) => {
    try {
      const res = await fetch(`/api/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setSelectedUserApps({ user: d.user, appAccess: d.appAccess }); setShowAppAccessModal(true); }
    } catch { setError('Error fetching user app access'); }
  };

  const updateAppAccess = async (appKey, isEnabled) => {
    if (!selectedUserApps) return;
    try {
      const res = await fetch(`/api/admin/users/${selectedUserApps.user.id}/app-access`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ appAccess: [{ appKey, isEnabled }] }) });
      if (res.ok) {
        setSuccess('App access updated');
        setSelectedUserApps(prev => ({ ...prev, appAccess: prev.appAccess.map(a => a.app_key === appKey ? { ...a, is_enabled: isEnabled ? 1 : 0 } : a) }));
        fetchUsers(pagination.page, searchTerm, roleFilter);
        setTimeout(() => setSuccess(''), 3000);
      } else setError('Failed to update app access');
    } catch { setError('Error updating app access'); }
  };

  useEffect(() => { fetchStats(); fetchUsers(); fetchAvailableApps(); }, []);
  useEffect(() => { const t = setTimeout(() => fetchUsers(1, searchTerm, roleFilter), 500); return () => clearTimeout(t); }, [searchTerm, roleFilter]);

  const rolePill = (role) => {
    const map = { admin: ['pill-purple', 'Admin'], doctor: ['pill-teal', 'Doctor'], patient: ['pill-blue', 'Patient'], ward_clerk: ['pill-orange', 'Ward Clerk'], hospital_admin: ['pill-green', 'Hosp. Admin'] };
    const [cls, lbl] = map[role] || ['pill-gray', role || 'User'];
    return <span className={`pill ${cls}`}>{lbl}</span>;
  };

  const NAV = [
    { key: 'dashboard', icon: 'dashboard', label: 'Dashboard' },
    { key: 'users', icon: 'group', label: 'User Management' },
    { key: 'email-test', icon: 'mail', label: 'Email Test' },
  ];

  return (
    <>
      <style>{ADMIN_STYLES}</style>
      <div className="admin-wrap">
        {/* ── Sidebar ── */}
        <aside className="admin-sidebar">
          <div className="sidebar-brand">
            <div className="sidebar-brand-inner">
              <div className="sidebar-logo">
                <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>medical_services</span>
              </div>
              <div className="sidebar-brand-text">
                <h1>InstantDr</h1>
                <p>Admin Console</p>
              </div>
            </div>
          </div>

          <nav className="sidebar-nav">
            <div className="sidebar-section-label">Main</div>
            {NAV.map(n => (
              <button key={n.key} className={`sidebar-item ${activeTab === n.key ? 'active' : ''}`} onClick={() => setActiveTab(n.key)}>
                <span className="material-symbols-outlined">{n.icon}</span>
                {n.label}
              </button>
            ))}

            <div className="sidebar-section-label" style={{ marginTop: 16 }}>Platform</div>
            <button className="sidebar-item" onClick={() => window.location.href = '/dashboard'}>
              <span className="material-symbols-outlined">home</span>Main Dashboard
            </button>
            <button className="sidebar-item" onClick={() => window.location.href = '/book-appointment'}>
              <span className="material-symbols-outlined">event</span>Appointments
            </button>

            <div className="sidebar-divider" />
            <button className="sidebar-item sidebar-footer-item" style={{ marginTop: 8 }} onClick={() => window.location.href = '/login'}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>logout</span>Sign Out
            </button>
          </nav>
        </aside>

        {/* ── Main ── */}
        <div className="admin-main">
          {/* ── Topbar ── */}
          <header className="admin-topbar">
            <div className="topbar-search">
              <span className="material-symbols-outlined">search</span>
              <input placeholder="Search users, appointments..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
            <div className="topbar-right">
              <div className="topbar-user">
                <div style={{ textAlign: 'right' }}>
                  <div className="topbar-user-name">Admin</div>
                  <div className="topbar-user-role">Administrator</div>
                </div>
                <div className="topbar-avatar">AD</div>
              </div>
            </div>
          </header>

          {/* ── Content ── */}
          <div className="admin-content">
            {error && <div className="alert alert-error"><span className="material-symbols-outlined" style={{ fontSize: 18 }}>warning</span>{error}<button onClick={() => setError('')} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16 }}>✕</button></div>}
            {success && <div className="alert alert-success"><span className="material-symbols-outlined" style={{ fontSize: 18 }}>check_circle</span>{success}</div>}

            {/* ── Dashboard Tab ── */}
            {activeTab === 'dashboard' && (
              <>
                <div className="page-header">
                  <div>
                    <div className="page-greeting">Good Evening</div>
                    <div className="page-title">Admin Dashboard</div>
                  </div>
                  <div className="page-actions">
                    <button className="btn btn-primary" onClick={() => setActiveTab('users')}>
                      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>person_add</span>Add User
                    </button>
                    <button className="btn btn-outline" onClick={() => setActiveTab('email-test')}>
                      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>mail</span>Test Email
                    </button>
                  </div>
                </div>

                {/* Hero */}
                <div className="hero-banner">
                  <div className="hero-deco" /><div className="hero-deco2" />
                  <div style={{ position: 'relative', zIndex: 2 }}>
                    <div className="hero-badge">
                      <div className="hero-pulse" />
                      <span>AI Platform Active</span>
                    </div>
                    <h2>Your AI-Powered Telehealth Platform</h2>
                    <p>Instant consultations, smart medical letters, HD video calls — all in one place.</p>
                  </div>
                </div>

                {/* Stats */}
                <div className="stats-grid">
                  {[
                    { icon: 'stethoscope', bg: '#e8ddff', color: '#4800b2', value: stats.totalUsers || 0, label: 'Total Users', note: 'all registered' },
                    { icon: 'description', bg: '#ffdbcf', color: '#6f2300', value: stats.adminUsers || 0, label: 'Admin Users', note: 'with admin access' },
                    { icon: 'group', bg: '#e3e0f4', color: '#5e5d6d', value: stats.recentRegistrations || 0, label: 'New This Month', note: 'registrations' },
                    { icon: 'apps', bg: '#dcfce7', color: '#15803d', value: stats.availableApps || 0, label: 'Available Apps', note: 'platform features' },
                  ].map((s, i) => (
                    <div className="stat-card" key={i}>
                      <div className="stat-card-head">
                        <div className="stat-icon" style={{ background: s.bg }}>
                          <span className="material-symbols-outlined" style={{ color: s.color }}>{s.icon}</span>
                        </div>
                        <span className="stat-trend trend-up">+5% <span className="material-symbols-outlined" style={{ fontSize: 11 }}>trending_up</span></span>
                      </div>
                      <div className="stat-value">{s.value}</div>
                      <div className="stat-label">{s.label}</div>
                      <div className="stat-note">{s.note}</div>
                    </div>
                  ))}
                </div>

                {/* AI Tools + Activity */}
                <div className="content-grid">
                  <div>
                    <div className="s-card">
                      <div className="s-card-header">
                        <div className="s-card-title">AI Tools</div>
                        <span className="pill pill-purple">{AI_TOOLS.length} active</span>
                      </div>
                      <div className="s-card-body">
                        <div className="tools-grid">
                          {AI_TOOLS.map(t => (
                            <div className="tool-card" key={t.name}>
                              <div className="tool-icon" style={{ background: t.bg }}>
                                <span className="material-symbols-outlined" style={{ color: t.color }}>{t.icon}</span>
                              </div>
                              <div className="tool-name">{t.name}</div>
                              <div className="tool-sub">{t.sub}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="s-card" style={{ marginBottom: 20 }}>
                      <div className="s-card-header">
                        <div className="s-card-title">Recent Activity</div>
                        <button style={{ color: '#4800b2', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', background: 'none', border: 'none', cursor: 'pointer' }}>View All</button>
                      </div>
                      <div className="s-card-body" style={{ padding: '0 22px 16px' }}>
                        {[
                          { icon: 'check_circle', bg: '#f0fdf4', color: '#22c55e', title: 'AI Consultation', desc: 'Completed', time: 'Just now' },
                          { icon: 'description', bg: '#fff7ed', color: '#f97316', title: 'Medical Letter', desc: 'Auto-generated', time: '12m ago' },
                          { icon: 'calendar_today', bg: '#eef2ff', color: '#6366f1', title: 'Appointment Booked', desc: 'Patient confirmed', time: '1h ago' },
                          { icon: 'videocam', bg: '#eff6ff', color: '#3b82f6', title: 'Video Call Ended', desc: 'Duration: 18 min', time: '3h ago' },
                        ].map((a, i) => (
                          <div className="activity-item" key={i}>
                            <div className="activity-icon" style={{ background: a.bg }}>
                              <span className="material-symbols-outlined" style={{ color: a.color }}>{a.icon}</span>
                            </div>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#191c1f' }}>{a.title}</div>
                              <div style={{ fontSize: 11, color: '#7a7488' }}>{a.desc}</div>
                              <div style={{ fontSize: 9, color: '#7a7488', opacity: 0.6, textTransform: 'uppercase', fontWeight: 700, marginTop: 3 }}>{a.time}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="s-card">
                      <div className="s-card-header"><div className="s-card-title">Platform Overview</div></div>
                      <div className="s-card-body">
                        {PLATFORMS.map(p => (
                          <div className="platform-item" key={p.name}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#191c1f' }}>{p.name}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span className={`pill ${p.tagClass}`} style={{ fontSize: 9 }}>{p.tag}</span>
                              <button style={{ background: '#f0e6ff', border: 'none', borderRadius: 6, padding: '3px 6px', cursor: 'pointer', color: '#4800b2' }}>
                                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>open_in_new</span>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Users */}
                <div className="s-card">
                  <div className="s-card-header">
                    <div className="s-card-title">Recent Users</div>
                    <button className="btn btn-sm btn-outline" onClick={() => setActiveTab('users')}>View All</button>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="admin-table">
                      <thead><tr><th>User</th><th>Role</th><th>Joined</th><th>Apps</th></tr></thead>
                      <tbody>
                        {users.slice(0, 6).map(u => (
                          <tr key={u.id}>
                            <td>
                              <div className="user-cell">
                                <div className="user-avatar" style={{ background: getAvatarColor(u.first_name || u.email) }}>{getInitials(`${u.first_name || ''} ${u.last_name || ''}`)}</div>
                                <div><div className="user-name">{u.first_name} {u.last_name}</div><div className="user-email">{u.email}</div></div>
                              </div>
                            </td>
                            <td>{rolePill(u.role)}</td>
                            <td style={{ color: '#7a7488', fontSize: 12 }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                            <td><span className="pill pill-gray">{u.app_count || 0} apps</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* ── Users Tab ── */}
            {activeTab === 'users' && (
              <>
                <div className="page-header">
                  <div>
                    <div className="page-greeting">Manage</div>
                    <div className="page-title">User Management</div>
                  </div>
                  <button className="btn btn-primary" onClick={() => { setEditingUser(null); setUserForm({ firstName: '', lastName: '', email: '', password: '', role: 'patient', specialisation: '', phone: '', selectedApps: [] }); setShowUserForm(true); }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>person_add</span>Add User
                  </button>
                </div>

                <div className="s-card">
                  <div className="s-card-header" style={{ flexWrap: 'wrap', gap: 12 }}>
                    <div className="s-card-title">All Users {pagination.total ? `(${pagination.total})` : ''}</div>
                    <div className="search-row">
                      <input className="admin-input" type="text" placeholder="🔍 Search users..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ minWidth: 200 }} />
                      <select className="admin-select" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                        <option value="">All Roles</option>
                        <option value="patient">Patient</option>
                        <option value="doctor">Doctor</option>
                        <option value="admin">Admin</option>
                        <option value="ward_clerk">Ward Clerk</option>
                        <option value="hospital_admin">Hospital Admin</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    {isLoading ? (
                      <div style={{ textAlign: 'center', padding: '48px', color: '#7a7488' }}>
                        <div style={{ width: 36, height: 36, border: '3px solid #e8ddff', borderTopColor: '#6200ee', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                        Loading users…
                      </div>
                    ) : (
                      <table className="admin-table">
                        <thead>
                          <tr><th>User</th><th>Role</th><th>Status</th><th>Apps</th><th>Actions</th></tr>
                        </thead>
                        <tbody>
                          {users.length === 0 ? (
                            <tr><td colSpan={5} style={{ textAlign: 'center', padding: '48px', color: '#7a7488' }}>No users found</td></tr>
                          ) : users.map(u => (
                            <tr key={u.id}>
                              <td>
                                <div className="user-cell">
                                  <div className="user-avatar" style={{ background: getAvatarColor(u.first_name || u.email) }}>{getInitials(`${u.first_name || ''} ${u.last_name || ''}`)}</div>
                                  <div>
                                    <div className="user-name">{u.first_name} {u.last_name}</div>
                                    <div className="user-email">{u.email}</div>
                                    {u.specialisation && <div style={{ fontSize: 10, color: '#7a7488' }}>{u.specialisation}</div>}
                                  </div>
                                </div>
                              </td>
                              <td>{rolePill(u.role)}</td>
                              <td><span className={`pill ${u.is_active ? 'pill-green' : 'pill-red'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                              <td>
                                <span className="pill pill-gray">{u.app_count > 0 ? `${u.app_count} apps` : 'None'}</span>
                              </td>
                              <td>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                  <button className="btn btn-sm btn-success" onClick={() => fetchUserAppAccess(u.id)}>
                                    <span className="material-symbols-outlined" style={{ fontSize: 12 }}>apps</span>Apps
                                  </button>
                                  <button className="btn btn-sm btn-outline" onClick={() => handleEditUser(u)}>
                                    <span className="material-symbols-outlined" style={{ fontSize: 12 }}>edit</span>Edit
                                  </button>
                                  <button className="btn btn-sm btn-danger" onClick={() => handleDeleteUser(u.id)}>
                                    <span className="material-symbols-outlined" style={{ fontSize: 12 }}>delete</span>Del
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  {pagination.pages > 1 && (
                    <div style={{ padding: '16px 22px', borderTop: '1px solid #edeef2', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: 12, color: '#7a7488' }}>
                        Showing {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                      </div>
                      <div className="pagination">
                        <button className="page-btn" disabled={pagination.page === 1} onClick={() => fetchUsers(pagination.page - 1, searchTerm, roleFilter)}>← Prev</button>
                        <span style={{ fontSize: 12, color: '#7a7488', padding: '0 8px' }}>{pagination.page} / {pagination.pages}</span>
                        <button className="page-btn" disabled={pagination.page === pagination.pages} onClick={() => fetchUsers(pagination.page + 1, searchTerm, roleFilter)}>Next →</button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ── Email Test Tab ── */}
            {activeTab === 'email-test' && (
              <>
                <div className="page-header">
                  <div>
                    <div className="page-greeting">Tools</div>
                    <div className="page-title">Email Test</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 24 }}>
                  <div className="s-card">
                    <div className="s-card-header">
                      <div className="s-card-title">Send Test Email</div>
                      <div style={{ background: '#e8ddff', borderRadius: 8, padding: '4px 10px' }}>
                        <span style={{ fontSize: 10, color: '#4800b2', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>EmailIt API</span>
                      </div>
                    </div>
                    <div className="s-card-body">
                      <div style={{ background: '#f8f9fd', borderRadius: 12, padding: 14, marginBottom: 20, border: '1px solid #e8ddff' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#4800b2', marginBottom: 4 }}>📌 Configuration</div>
                        <div style={{ fontSize: 12, color: '#494456' }}>From: <code style={{ background: '#e8ddff', padding: '2px 6px', borderRadius: 4, color: '#4800b2' }}>instantdrconsult@optimail.pro</code></div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Email Template</label>
                        <select className="form-input" value={emailTest.template} onChange={e => setEmailTest(p => ({ ...p, template: e.target.value }))}>
                          <option value="custom">✏️ Custom Message</option>
                          <optgroup label="Appointment Templates">
                            <option value="appointment_booked_patient">📅 Appointment Booked → Patient</option>
                            <option value="appointment_booked_doctor">🩺 Appointment Booked → Doctor</option>
                            <option value="appointment_confirmed">✅ Appointment Confirmed</option>
                            <option value="appointment_cancelled">❌ Appointment Cancelled</option>
                            <option value="appointment_reminder">⏰ Appointment Reminder</option>
                            <option value="appointment_completed">🎉 Appointment Completed</option>
                          </optgroup>
                        </select>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div className="form-group">
                          <label className="form-label">Recipient Email *</label>
                          <input className="form-input" type="email" placeholder="test@example.com" value={emailTest.to} onChange={e => setEmailTest(p => ({ ...p, to: e.target.value }))} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Recipient Name</label>
                          <input className="form-input" type="text" placeholder="John Doe" value={emailTest.toName} onChange={e => setEmailTest(p => ({ ...p, toName: e.target.value }))} />
                        </div>
                      </div>
                      {emailTest.template === 'custom' && (
                        <>
                          <div className="form-group">
                            <label className="form-label">Subject *</label>
                            <input className="form-input" type="text" value={emailTest.subject} onChange={e => setEmailTest(p => ({ ...p, subject: e.target.value }))} />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Message Body *</label>
                            <textarea className="form-input" rows={5} value={emailTest.body} onChange={e => setEmailTest(p => ({ ...p, body: e.target.value }))} style={{ fontFamily: 'monospace', resize: 'vertical' }} />
                          </div>
                        </>
                      )}
                      <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '12px' }} disabled={emailTestLoading || !emailTest.to}
                        onClick={async () => {
                          setEmailTestLoading(true); setEmailTestResult(null);
                          try {
                            const res = await fetch('/api/admin/email-test.php', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(emailTest) });
                            setEmailTestResult(await res.json());
                          } catch (err) { setEmailTestResult({ success: false, message: 'Network error: ' + err.message }); }
                          finally { setEmailTestLoading(false); }
                        }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{emailTestLoading ? 'hourglass_empty' : 'send'}</span>
                        {emailTestLoading ? 'Sending…' : 'Send Test Email'}
                      </button>

                      {emailTestResult && (
                        <div style={{ marginTop: 16, padding: '14px 16px', borderRadius: 12, background: emailTestResult.success ? '#f0fdf4' : '#fef2f2', border: `1px solid ${emailTestResult.success ? '#bbf7d0' : '#fecaca'}` }}>
                          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                            <span className="material-symbols-outlined" style={{ color: emailTestResult.success ? '#22c55e' : '#ef4444', fontSize: 22 }}>{emailTestResult.success ? 'check_circle' : 'cancel'}</span>
                            <div>
                              <div style={{ fontWeight: 700, color: emailTestResult.success ? '#15803d' : '#b91c1c', fontSize: 13 }}>{emailTestResult.success ? 'Email Sent!' : 'Failed to Send'}</div>
                              <div style={{ fontSize: 12, color: emailTestResult.success ? '#16a34a' : '#dc2626', marginTop: 2 }}>{emailTestResult.message}</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="s-card">
                    <div className="s-card-header"><div className="s-card-title">Available Templates</div></div>
                    <div className="s-card-body" style={{ padding: '12px 20px' }}>
                      {[
                        { icon: '📅', title: 'Appointment Booked (Patient)', desc: 'Date, time, doctor name & type' },
                        { icon: '🩺', title: 'Appointment Booked (Doctor)', desc: 'Patient name, date & complaint' },
                        { icon: '✅', title: 'Appointment Confirmed', desc: 'Dashboard link to join video' },
                        { icon: '❌', title: 'Appointment Cancelled', desc: 'Cancellation reason included' },
                        { icon: '⏰', title: 'Appointment Reminder', desc: '24-hour reminder with video link' },
                        { icon: '🎉', title: 'Appointment Completed', desc: 'Links to consultation letters' },
                      ].map((t, i) => (
                        <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: i < 5 ? '1px solid #f2f3f7' : 'none' }}>
                          <span style={{ fontSize: 18 }}>{t.icon}</span>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#191c1f' }}>{t.title}</div>
                            <div style={{ fontSize: 11, color: '#7a7488', marginTop: 2 }}>{t.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Add/Edit User Modal ── */}
        {showUserForm && (
          <div className="modal-bg" onClick={e => e.target === e.currentTarget && setShowUserForm(false)}>
            <div className="modal">
              <div className="modal-header">
                <div className="modal-title">{editingUser ? 'Edit User' : 'Add New User'}</div>
                <button className="modal-close" onClick={() => setShowUserForm(false)}>✕</button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleUserSubmit}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">First Name</label>
                      <input className="form-input" type="text" name="firstName" placeholder="First name" value={userForm.firstName} onChange={e => setUserForm(p => ({ ...p, firstName: e.target.value }))} required />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Last Name</label>
                      <input className="form-input" type="text" name="lastName" placeholder="Last name" value={userForm.lastName} onChange={e => setUserForm(p => ({ ...p, lastName: e.target.value }))} required />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input className="form-input" type="email" placeholder="email@example.com" value={userForm.email} onChange={e => setUserForm(p => ({ ...p, email: e.target.value }))} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Password {editingUser && '(leave blank to keep)'}</label>
                    <input className="form-input" type="password" placeholder={editingUser ? 'New password (optional)' : 'Password'} value={userForm.password} onChange={e => setUserForm(p => ({ ...p, password: e.target.value }))} required={!editingUser} minLength={6} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <select className="form-input" value={userForm.role} onChange={e => setUserForm(p => ({ ...p, role: e.target.value }))} required>
                      <option value="patient">Patient</option>
                      <option value="ward_clerk">Ward Clerk</option>
                      <option value="doctor">Doctor</option>
                      <option value="hospital_admin">Hospital Admin</option>
                      <option value="admin">System Admin</option>
                      <option value="user">User (Legacy)</option>
                    </select>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Specialisation</label>
                      <input className="form-input" type="text" placeholder="Optional" value={userForm.specialisation} onChange={e => setUserForm(p => ({ ...p, specialisation: e.target.value }))} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Phone</label>
                      <input className="form-input" type="tel" placeholder="Optional" value={userForm.phone} onChange={e => setUserForm(p => ({ ...p, phone: e.target.value }))} />
                    </div>
                  </div>

                  {availableApps.length > 0 && (
                    <div className="form-group">
                      <label className="form-label">Assign Apps ({userForm.selectedApps.length} selected)</label>
                      <div style={{ maxHeight: 160, overflowY: 'auto', background: '#f8f9fd', borderRadius: 10, padding: '4px 0' }}>
                        {availableApps.map(app => (
                          <label key={app.app_key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', borderRadius: 8 }}>
                            <input type="checkbox" checked={userForm.selectedApps.includes(app.app_key)} onChange={e => setUserForm(p => ({ ...p, selectedApps: e.target.checked ? [...p.selectedApps, app.app_key] : p.selectedApps.filter(k => k !== app.app_key) }))} style={{ accentColor: '#6200ee' }} />
                            <span style={{ fontSize: 18 }}>{app.icon || '📱'}</span>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#191c1f' }}>{app.app_name}</div>
                              <div style={{ fontSize: 10, color: '#7a7488' }}>{app.description}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                    <button type="submit" className="btn btn-primary" disabled={isLoading} style={{ flex: 1, justifyContent: 'center' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{isLoading ? 'hourglass_empty' : 'save'}</span>
                      {isLoading ? 'Saving…' : (editingUser ? 'Update User' : 'Create User')}
                    </button>
                    <button type="button" className="btn btn-neutral" onClick={() => { setShowUserForm(false); setEditingUser(null); }}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* ── App Access Modal ── */}
        {showAppAccessModal && selectedUserApps && (
          <div className="modal-bg" onClick={e => e.target === e.currentTarget && setShowAppAccessModal(false)}>
            <div className="modal modal-lg">
              <div className="modal-header">
                <div>
                  <div className="modal-title">App Access — {selectedUserApps.user.first_name} {selectedUserApps.user.last_name}</div>
                  <div style={{ fontSize: 12, color: '#7a7488', marginTop: 2 }}>{selectedUserApps.user.email} · {selectedUserApps.user.role}</div>
                </div>
                <button className="modal-close" onClick={() => { setShowAppAccessModal(false); setSelectedUserApps(null); }}>✕</button>
              </div>
              <div className="modal-body">
                {selectedUserApps.appAccess && selectedUserApps.appAccess.length > 0 ? (
                  selectedUserApps.appAccess.map(app => (
                    <div className="app-item" key={app.app_key}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 12, background: '#f0e6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{app.icon || '📱'}</div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#191c1f' }}>{app.app_name}</div>
                          <div style={{ fontSize: 11, color: '#7a7488' }}>{app.description || 'No description'}</div>
                          {app.granted_at && <div style={{ fontSize: 10, color: '#7a7488', opacity: 0.7, marginTop: 2 }}>Granted: {new Date(app.granted_at).toLocaleDateString()}</div>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 11, color: app.is_enabled ? '#15803d' : '#7a7488', fontWeight: 600 }}>{app.is_enabled ? 'Enabled' : 'Disabled'}</span>
                        <label className="toggle">
                          <input type="checkbox" checked={!!app.is_enabled} onChange={e => updateAppAccess(app.app_key, e.target.checked)} />
                          <span className="toggle-track" />
                        </label>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#7a7488' }}>No apps available</div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                  <button className="btn btn-neutral" onClick={() => { setShowAppAccessModal(false); setSelectedUserApps(null); }}>Close</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}